<template>
  <div class="category-selector">
    <!-- Search field for categories -->
    <div class="form-group mb-2" v-if="showSearch">
      <input
        type="text"
        class="form-control"
        placeholder="Search categories..."
        v-model="searchQuery"
      />
    </div>

    <!-- Categories section -->
    <div class="categories-container">
      <div
        v-for="category in filteredCategories"
        :key="category._id"
        class="category-group mb-3"
      >
        <div class="category-header d-flex align-items-center">
          <div class="custom-control custom-checkbox">
            <input
              type="checkbox"
              class="custom-control-input"
              :id="'cat-' + category._id"
              :value="category._id"
              :checked="isSelected(category._id, selectedCategories)"
              @change="toggleCategory(category._id)"
            />
            <label
              class="custom-control-label font-weight-bold"
              :for="'cat-' + category._id"
            >
              {{ category.business_category_name }}
            </label>
          </div>
        </div>

        <!-- Subcategories section -->
        <div
          class="subcategories-container pl-4 mt-2"
          v-if="getCategorySubcategories(category._id).length"
        >
          <div
            v-for="subcategory in getCategorySubcategories(category._id)"
            :key="subcategory._id"
            class="subcategory-item"
          >
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                :id="'subcat-' + subcategory._id"
                :value="subcategory._id"
                :checked="isSelected(subcategory._id, selectedSubcategories)"
                @change="toggleSubcategory(subcategory._id, category._id)"
              />
              <label
                class="custom-control-label"
                :for="'subcat-' + subcategory._id"
              >
                {{ subcategory.business_subcategory_name }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Selected categories display -->
    <div v-if="selectedCategories.length > 0" class="selected-categories mt-3">
      <label class="form-control-label mb-1">Selected Categories:</label>
      <div class="d-flex flex-wrap">
        <span
          v-for="(catId, index) in selectedCategories"
          :key="'selected-cat-' + index"
          class="badge badge-primary mr-1 mb-1"
        >
          {{ getCategoryName(catId) }}
          <i
            class="fa fa-times ml-1 cursor-pointer"
            @click="removeCategory(catId)"
          ></i>
        </span>
      </div>
    </div>

    <!-- Selected subcategories display -->
    <div
      v-if="selectedSubcategories.length > 0"
      class="selected-subcategories mt-3"
    >
      <label class="form-control-label mb-1">Selected Subcategories:</label>
      <div class="d-flex flex-wrap">
        <span
          v-for="(subcatId, index) in selectedSubcategories"
          :key="'selected-subcat-' + index"
          class="badge badge-info mr-1 mb-1"
        >
          {{ getSubcategoryName(subcatId) }}
          <i
            class="fa fa-times ml-1 cursor-pointer"
            @click="removeSubcategory(subcatId)"
          ></i>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'BusinessCategorySelector',
  props: {
    initialSelectedCategories: {
      type: Array,
      default: () => [],
    },
    initialSelectedSubcategories: {
      type: Array,
      default: () => [],
    },
    showSelectedItems: {
      type: Boolean,
      default: true,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    businessType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      allCategories: [], // Store all categories
      categories: [], // For backward compatibility
      allSubcategories: {},
      selectedCategories: [...this.initialSelectedCategories],
      selectedSubcategories: [...this.initialSelectedSubcategories],
      searchQuery: '',
      loading: false,
    };
  },
  computed: {
    categoriesByBusinessType() {
      // Filter categories based on business type
      if (!this.businessType || this.businessType === 'Designer + Supplier') {
        // Both or no type selected - show all categories
        return this.allCategories;
      }

      if (this.businessType === 'Designer') {
        // Filter to only show 'Architectural & Engineering Consultancy' for designers
        return this.allCategories.filter(
          (cat) =>
            cat.business_category_name ===
            'Architectural & Engineering Consultancy'
        );
      } else if (this.businessType === 'Supplier') {
        // Filter to show all categories EXCEPT 'Architectural & Engineering Consultancy' for suppliers
        return this.allCategories.filter(
          (cat) =>
            cat.business_category_name !==
            'Architectural & Engineering Consultancy'
        );
      }

      return this.allCategories;
    },

    filteredCategories() {
      const categoriesToFilter = this.categoriesByBusinessType;

      if (!this.searchQuery) return categoriesToFilter;

      return categoriesToFilter.filter((category) => {
        const matchesCategory = category.business_category_name
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase());

        const hasMatchingSubcategories = this.getCategorySubcategories(
          category._id
        ).some((subcategory) =>
          subcategory.business_subcategory_name
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
        );

        return matchesCategory || hasMatchingSubcategories;
      });
    },
  },
  watch: {
    initialSelectedCategories: {
      handler(newVal) {
        this.selectedCategories = [...newVal];
      },
      deep: true,
    },
    initialSelectedSubcategories: {
      handler(newVal) {
        this.selectedSubcategories = [...newVal];
      },
      deep: true,
    },
    selectedCategories: {
      handler(newVal) {
        this.$emit('update:categories', newVal);
        this.fetchSubcategoriesForCategories();
      },
      deep: true,
    },
    selectedSubcategories: {
      handler(newVal) {
        this.$emit('update:subcategories', newVal);
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchCategories();
  },
  methods: {
    fetchCategories() {
      this.loading = true;

      axios
        .post(process.env.VUE_APP_API_ENDPOINT + 'read_business_category', {
          status: 'approved',
        })
        .then((response) => {
          if (response.data.details) {
            this.allCategories = response.data.details;
            this.categories = response.data.details; // For backward compatibility

            // Fetch subcategories for any pre-selected categories
            if (this.selectedCategories.length > 0) {
              this.fetchSubcategoriesForCategories();
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching categories:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    fetchSubcategoriesForCategories() {
      if (this.selectedCategories.length === 0) return;

      const promises = this.selectedCategories.map((categoryId) => {
        return axios.post(
          process.env.VUE_APP_API_ENDPOINT + 'read_business_subcategory',
          { business_category_oid: categoryId },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'nabnee#' + localStorage.getItem('accessToken'),
              _id: localStorage.getItem('_id'),
            },
          }
        );
      });

      Promise.all(promises)
        .then((responses) => {
          responses.forEach((response, index) => {
            const categoryId = this.selectedCategories[index];

            if (response.data.details) {
              // Store subcategories by category ID
              this.$set(
                this.allSubcategories,
                categoryId,
                response.data.details
              );
            }
          });
        })
        .catch((error) => {
          console.error('Error fetching subcategories:', error);
        });
    },

    getCategorySubcategories(categoryId) {
      return this.allSubcategories[categoryId] || [];
    },

    isSelected(id, selectedArray) {
      return selectedArray.includes(id);
    },

    toggleCategory(categoryId) {
      const index = this.selectedCategories.indexOf(categoryId);

      if (index === -1) {
        // Add category
        this.selectedCategories.push(categoryId);

        // Fetch subcategories for this category
        this.fetchSubcategoryForCategory(categoryId);
      } else {
        // Remove category
        this.selectedCategories.splice(index, 1);

        // Remove all subcategories belonging to this category
        if (this.allSubcategories[categoryId]) {
          const subcategoryIds = this.allSubcategories[categoryId].map(
            (sub) => sub._id
          );
          this.selectedSubcategories = this.selectedSubcategories.filter(
            (id) => !subcategoryIds.includes(id)
          );

          // Remove subcategories from memory
          this.$delete(this.allSubcategories, categoryId);
        }
      }
    },

    fetchSubcategoryForCategory(categoryId) {
      axios
        .post(
          process.env.VUE_APP_API_ENDPOINT + 'read_business_subcategory',
          { business_category_oid: categoryId },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'nabnee#' + localStorage.getItem('accessToken'),
              _id: localStorage.getItem('_id'),
            },
          }
        )
        .then((response) => {
          if (response.data.details) {
            this.$set(this.allSubcategories, categoryId, response.data.details);
          }
        })
        .catch((error) => {
          console.error(
            `Error fetching subcategories for category ${categoryId}:`,
            error
          );
        });
    },

    toggleSubcategory(subcategoryId, categoryId) {
      const index = this.selectedSubcategories.indexOf(subcategoryId);

      if (index === -1) {
        // Add subcategory
        this.selectedSubcategories.push(subcategoryId);

        // Make sure parent category is selected
        if (!this.selectedCategories.includes(categoryId)) {
          this.selectedCategories.push(categoryId);
        }
      } else {
        // Remove subcategory
        this.selectedSubcategories.splice(index, 1);
      }
    },

    removeCategory(categoryId) {
      // Remove category
      const catIndex = this.selectedCategories.indexOf(categoryId);
      if (catIndex !== -1) {
        this.selectedCategories.splice(catIndex, 1);
      }

      // Remove all subcategories belonging to this category
      if (this.allSubcategories[categoryId]) {
        const subcategoryIds = this.allSubcategories[categoryId].map(
          (sub) => sub._id
        );
        this.selectedSubcategories = this.selectedSubcategories.filter(
          (id) => !subcategoryIds.includes(id)
        );

        // Remove subcategories from memory
        this.$delete(this.allSubcategories, categoryId);
      }
    },

    removeSubcategory(subcategoryId) {
      const index = this.selectedSubcategories.indexOf(subcategoryId);
      if (index !== -1) {
        this.selectedSubcategories.splice(index, 1);
      }
    },

    getCategoryName(categoryId) {
      const category = this.allCategories.find((c) => c._id === categoryId);
      return category ? category.business_category_name : 'Unknown';
    },

    getSubcategoryName(subcategoryId) {
      for (const categoryId in this.allSubcategories) {
        const subcategory = this.allSubcategories[categoryId].find(
          (s) => s._id === subcategoryId
        );
        if (subcategory) {
          return subcategory.business_subcategory_name;
        }
      }
      return 'Unknown';
    },
  },
};
</script>

<style scoped>
.category-selector {
  background-color: #fff;
  border-radius: 8px;
  padding: 15px;
}

.category-group {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 15px;
}

.category-group:last-child {
  border-bottom: none;
}

.category-header {
  cursor: pointer;
}

.subcategories-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.subcategory-item {
  margin-bottom: 8px;
  width: calc(50% - 10px);
}

.badge {
  padding: 8px 12px;
  border-radius: 50px;
  font-weight: 500;
  font-size: 0.85rem;
}

.badge i {
  cursor: pointer;
}

.badge i:hover {
  opacity: 0.7;
}

.selected-categories,
.selected-subcategories {
  padding: 10px 0;
}

@media (max-width: 768px) {
  .subcategory-item {
    width: 100%;
  }
}
</style>
